import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Typography,
  Grid,
  Chip,
  Stack,
  Switch,
  FormControlLabel,
  Snackbar,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  AdminPanelSettings,
  Visibility,
  HowToReg,
  ContentCopy,
  CalendarToday,
  LocationOn,
  Event as EventIcon,
} from '@mui/icons-material';
import axios from 'axios';

interface Event {
  id: number;
  title: string;
  description: string;
  date: string;
  location: string;
  slug: string;
  rsvp_cutoff_date?: string;
  wallpaper?: string;
  wallpaper_position?: string;
  wallpaper_zoom?: number;
}

const EventList: React.FC = () => {
  const [events, setEvents] = useState<Event[]>([]);
  const navigate = useNavigate();
  const [hideClosed, setHideClosed] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [copyingId, setCopyingId] = useState<number | null>(null);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get('/api/events');
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  const isEventOpen = (event: Event) => {
    if (!event.rsvp_cutoff_date) return true;
    const cutoffDate = new Date(event.rsvp_cutoff_date);
    return new Date() < cutoffDate;
  };

  const handleCopyLink = async (event: Event) => {
    if (copyingId !== null) return;
    setCopyingId(event.id);
    try {
      // Fetch at copy time so code regeneration cannot leave a stale card link.
      const { data } = await axios.get(`/api/events/${encodeURIComponent(event.slug)}/invitation-code`);
      if (typeof data?.invitation_code !== 'string' || !data.invitation_code) throw new Error('Missing code');
      const rsvpLink = `${window.location.origin}/rsvp/events/${encodeURIComponent(event.slug)}?code=${encodeURIComponent(data.invitation_code)}`;
      await navigator.clipboard.writeText(rsvpLink);
      setSnackbarMessage('RSVP invitation link copied to clipboard!');
    } catch {
      setSnackbarMessage('Failed to copy invitation link. Try again or open Manage.');
    } finally {
      setCopyingId(null);
      setOpenSnackbar(true);
    }
  };

  return (
    <Box>
      {/* Page header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
        <Typography variant="h4" component="h1">
          Events
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={hideClosed}
              onChange={(_e, checked) => setHideClosed(checked)}
              color="primary"
              size="small"
            />
          }
          label={<Typography variant="body2" color="text.secondary">Hide closed</Typography>}
        />
      </Box>

      {/* Event cards grid */}
      <Grid container spacing={3}>
        {events
          .filter(event => !hideClosed || isEventOpen(event))
          .sort((a, b) => {
            const aOpen = isEventOpen(a), bOpen = isEventOpen(b);
            if (aOpen !== bOpen) return aOpen ? -1 : 1;
            return new Date(a.date).getTime() - new Date(b.date).getTime();
          })
          .map((event) => {
            const open = isEventOpen(event);
            return (
              <Grid item xs={12} sm={6} md={4} key={event.id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    opacity: open ? 1 : 0.6,
                    cursor: 'pointer',
                  }}
                  onClick={() => navigate(`/view/events/${event.slug}`)}
                >
                  {/* Card hero image */}
                  {event.wallpaper ? (() => {
                    const pos = (event.wallpaper_position || '50% 50%').split(/\s+/);
                    const px = parseFloat(pos[0]) || 50;
                    const py = parseFloat(pos[1]) || 50;
                    const z = event.wallpaper_zoom ?? 1;
                    return (
                      <Box sx={{ height: 180, overflow: 'hidden', position: 'relative' }}>
                        <Box
                          component="img"
                          src={event.wallpaper}
                          alt={event.title}
                          sx={{
                            position: 'absolute',
                            left: `${px}%`, top: `${py}%`,
                            transform: `translate(-${px}%, -${py}%)`,
                            width: `${100 * z}%`, height: 'auto',
                            display: 'block',
                          }}
                        />
                      </Box>
                    );
                  })() : (
                    <Box
                      sx={{
                        height: 180,
                        bgcolor: 'primary.main',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <EventIcon sx={{ fontSize: 56, color: 'primary.contrastText', opacity: 0.7 }} />
                    </Box>
                  )}

                  <CardContent sx={{ flexGrow: 1, p: 2.5, pb: 1 }}>
                    {/* Title + status badge */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                      <Typography variant="h6" component="h2" sx={{ lineHeight: 1.3, pr: 1 }}>
                        {event.title}
                      </Typography>
                      <Chip
                        label={open ? 'Open' : 'Closed'}
                        color={open ? 'success' : 'default'}
                        size="small"
                        variant={open ? 'filled' : 'outlined'}
                        sx={{ flexShrink: 0 }}
                      />
                    </Box>

                    {/* Date + location */}
                    <Stack spacing={0.75}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarToday sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {new Date(event.date).toLocaleDateString(undefined, {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                          })}
                        </Typography>
                      </Box>
                      {event.location && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LocationOn sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary" noWrap>
                            {event.location}
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                  </CardContent>

                  {/* Action buttons */}
                  <CardActions sx={{ px: 2.5, pb: 2, pt: 0.5, gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
                    {open && (
                      <Button
                        size="small"
                        variant="text"
                        startIcon={<HowToReg />}
                        onClick={() => navigate(`/rsvp/events/${event.slug}`)}
                      >
                        RSVP
                      </Button>
                    )}
                    <Button
                      size="small"
                      variant="text"
                      startIcon={<Visibility />}
                      onClick={() => navigate(`/view/events/${event.slug}`)}
                    >
                      View
                    </Button>
                    <Box sx={{ flexGrow: 1 }} />
                    <Tooltip title="Copy RSVP link">
                      <IconButton size="small" aria-label={`Copy RSVP invitation link for ${event.title}`} disabled={copyingId !== null} onClick={() => handleCopyLink(event)}>
                        <ContentCopy sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Manage event">
                      <IconButton
                        size="small"
                        onClick={() => { window.location.href = `/admin/events/${event.slug}`; }}
                      >
                        <AdminPanelSettings sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
      </Grid>

      {/* Empty state */}
      {events.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <EventIcon sx={{ fontSize: 64, color: 'text.secondary', opacity: 0.4, mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No events yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Create your first event to get started.
          </Typography>
          <Button variant="contained" onClick={() => { window.location.href = '/create'; }}>
            Create Event
          </Button>
        </Box>
      )}

      <Snackbar open={openSnackbar} autoHideDuration={4000} onClose={() => setOpenSnackbar(false)}>
        <Alert onClose={() => setOpenSnackbar(false)} severity="success" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EventList;
